import { parse } from 'date-fns';
import type React from 'react';
import { memo, useCallback, useEffect, useRef, useState, type FocusEvent, type MouseEvent } from 'react';
import { useTheme } from 'styled-components';

import type { DateFnInput } from '../../utils/date';
import { isValidDateInput } from '../../utils/date';
import { IconButton } from '../Button';
import { FormControlSizes, Input, type FormControlProps } from '../Form';
import { Icon, IconName } from '../Icons';
import { Popover, usePopoverState } from '../Popover';
import { DateTimePickerWrapper } from './styles';

import { defineMessages, useIntl } from 'react-intl';
import { useDynamicCallback } from '../../hooks';
import { logger } from '../../utils';
import { DateTimePickerContent, type DateTimePickerContentProps, type SelectionOrigin } from './DateTimePickerContent';
import { DEFAULT_SHORTCUTS, getFormatter, shouldEmitChange, validateMaxDate, validateMinDate } from './utils';

const messages = defineMessages({
  ariaLabelForClearValueButton: {
    defaultMessage: 'Clear value',
    id: 'DateTimePicker.ariaLabelForClearValueButton',
  },
  ariaLabelForValueInput: {
    defaultMessage: 'Value',
    id: 'DateTimePicker.ariaLabelForValueInput',
  },
});

export type DateTimePickerProps = {
  /** Current value of the date / time picker */
  value: Date | null;
  /** Change event handler */
  onChange: (value: Date | null) => void;
  /** Portalize component: @default: true */
  portalize?: boolean;
} & Omit<FormControlProps<HTMLInputElement>, 'onChange' | 'value'> &
  Pick<
    DateTimePickerContentProps,
    | 'showCalendar'
    | 'showShortcuts'
    | 'showTimePicker'
    | 'showMilliseconds'
    | 'shortcuts'
    | 'timePickerVariant'
    | 'timeSelectorIntervalMinutes'
    | 'customShortcutResolver'
    | 'customEOD'
    | 'minValue'
    | 'maxValue'
    | 'defaultSelected'
  >;

/**
 * Date / Time Picker component
 */
export const DateTimePicker = memo(function DateTimePicker({
  onChange,
  onBlur,
  onFocus,
  value,
  showCalendar = true,
  showShortcuts = true,
  showTimePicker = true,
  showMilliseconds = false,
  shortcuts = DEFAULT_SHORTCUTS,
  className,
  style,
  disabled,
  minValue,
  maxValue,
  defaultSelected,
  customShortcutResolver,
  customEOD,
  timePickerVariant = 'picker',
  timeSelectorIntervalMinutes = 60,
  portalize = true,
  size,
  clearable = true,
  onClick,
  ...props
}: DateTimePickerProps) {
  const { spacing4 } = useTheme();
  const { formatMessage } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const controlRef = useRef<HTMLSpanElement>(null);
  const timeSelectorDropdownContentRef = useRef<HTMLDivElement>(null);
  const popover = usePopoverState({
    trigger: '',
    placement: 'bottom-end',
    delay: undefined,
    usePortal: portalize,
    onClickOutside: e => {
      const path = e.composedPath();
      const eventTarget = e.target;
      const insideTimeDropdown =
        timePickerVariant === 'selector' &&
        !!timeSelectorDropdownContentRef.current &&
        path.includes(timeSelectorDropdownContentRef.current);
      const insideControl =
        !!controlRef.current &&
        (path.includes(controlRef.current) ||
          (eventTarget instanceof Node && controlRef.current.contains(eventTarget)));
      if (insideControl || insideTimeDropdown) {
        return;
      }

      close();
    },
  });

  const dateFormatter = useCallback(
    (date: DateFnInput | null) => {
      if (date == null) {
        return '';
      }
      try {
        const formatter = getFormatter(showMilliseconds, showTimePicker);
        return formatter(date);
      } catch (e) {
        return '';
      }
    },
    [showMilliseconds, showTimePicker]
  );

  // Either parses as a Date (without time), or with date + time depending on inputs.
  const dateParser = useCallback(
    (text: string) => {
      let parseString = 'yyyy-MM-dd';
      let pattern = /\d\d\d\d-\d\d-\d\d/;
      if (showTimePicker) {
        parseString += ` HH:mm:ss${showMilliseconds ? '.SSS' : ''}`;
        pattern = showMilliseconds ? /\d\d\d\d-\d\d-\d\d \d\d:\d\d:\d\d.\d\d\d/ : /\d\d\d\d-\d\d-\d\d \d\d:\d\d:\d\d/;
      }
      if (text.match(pattern)) {
        return parse(text, parseString, new Date());
      } else {
        return new Date('invalid');
      }
    },
    [showTimePicker, showMilliseconds]
  );

  const [inputValue, setInputValue] = useState<string>(dateFormatter(value) ?? '');

  const inputValueRef = useRef(inputValue);
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    if (value != null && !isValidDateInput(value)) {
      logger.error(new Error(`Received invalid date '${value}' in date picker`), {
        extra: {
          inputValue: inputValueRef.current,
        },
      });
    }
  }, [value, inputValue, dateFormatter]);

  const [showClear, setShowClear] = useState(value !== null && isValidDateInput(value));
  const { open, close } = popover;

  useEffect(() => {
    setShowClear(value !== null && isValidDateInput(value));

    // Whenever value changes, update the inputValue to reflect it.
    if (value == null) {
      setInputValue('');
    } else if (isValidDateInput(value)) {
      setInputValue(dateFormatter(value) ?? '');
    }
  }, [value, dateFormatter]);

  // After a shortcut closes a portaled picker (e.g. inside a Modal FocusLock), focus can
  // return to the input and would otherwise immediately reopen it.
  const suppressOpenOnFocusRef = useRef(false);

  const handleInputFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (!suppressOpenOnFocusRef.current) {
        open();
      }
      suppressOpenOnFocusRef.current = false;
      if (onFocus != null) {
        onFocus(e);
      }
    },
    [open, onFocus]
  );

  const handleInputClick = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      suppressOpenOnFocusRef.current = false;
      if (!popover.isOpen) {
        open();
      }
      onClick?.(e);
    },
    [popover.isOpen, open, onClick]
  );

  // When blurring the input field, set to the string form of latest valid date (either a date or empty string)
  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const date = dateParser(e.target.value);
      if (!isValidDateInput(date)) {
        setInputValue(dateFormatter(value) ?? '');
      }

      if (onBlur) {
        onBlur(e);
      }
    },
    [dateFormatter, value, onBlur, dateParser]
  );

  const maybeEmitChange = useCallback(
    (maybeChangedDate: Date | null) => {
      if (maybeChangedDate && minValue) {
        maybeChangedDate = validateMinDate(maybeChangedDate, minValue, showTimePicker);
      }

      if (maybeChangedDate && maxValue) {
        maybeChangedDate = validateMaxDate(maybeChangedDate, maxValue, showTimePicker);
      }

      if (shouldEmitChange(value, maybeChangedDate)) {
        onChange(maybeChangedDate);
      }
    },
    [onChange, value, minValue, maxValue, showTimePicker]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && popover.isOpen) {
      // Close popover and stop propagation so parent modal/dialog is not closed
      // (SearchSelect does the same for its dropdown - see handleDropdownKeyDown)
      close();
      event.stopPropagation();
      return;
    }
    if (event.key === 'Tab') {
      if (inputRef.current && inputRef.current.contains(document.activeElement)) {
        close();
        return;
      }
    }
  };

  const handleTabOut = useDynamicCallback(() => {
    inputRef.current?.focus();
    close();
  });

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      const text = e.target.value;
      setInputValue(text);

      if (text === '') {
        if (clearable) {
          maybeEmitChange(null);
        }
        return;
      }

      try {
        // This works for 99% of cases, but see link to bug below.
        // For example, 2022-01-01 12:31:5 (missing digit at the end) will pass with "05" seconds when it should have been an invalid date
        // https://github.com/date-fns/date-fns/issues/1924
        const date = dateParser(text);
        if (isValidDateInput(date)) {
          maybeEmitChange(date);
          return;
        }
      } catch (e) {
        // Do nothing
        if (clearable) {
          maybeEmitChange(null);
        }
      }
    },
    [maybeEmitChange, dateParser, clearable]
  );

  const handleSelection = useDynamicCallback((date: Date | null, from: SelectionOrigin) => {
    if (isValidDateInput(date)) {
      setInputValue(dateFormatter(date) ?? '');
    }
    maybeEmitChange(date);

    const shouldClose = !showTimePicker || from === 'shortcut';
    if (shouldClose) {
      suppressOpenOnFocusRef.current = true;
      close();
    }
  });

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      maybeEmitChange(null);
      setInputValue('');
      return false;
    },
    [maybeEmitChange]
  );

  const clearDisabled = disabled || !showClear || !clearable;

  return (
    <DateTimePickerWrapper ref={controlRef} className={className} style={style} onKeyDown={handleKeyDown}>
      <Popover {...popover}>
        <Input
          {...props}
          size={size}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          value={inputValue}
          disabled={disabled}
          aria-label={formatMessage(messages.ariaLabelForValueInput)}
          ref={inputRef}
          suffix={
            <>
              <IconButton
                ghost
                round
                icon={IconName.Close}
                onClick={handleClear}
                style={{ visibility: clearDisabled ? 'hidden' : 'visible' }}
                disabled={clearDisabled}
                aria-label={formatMessage(messages.ariaLabelForClearValueButton)}
                size={((size ?? FormControlSizes.Default) - 0.5) as FormControlSizes}
                data-testid="date-time-picker-clear-button"
              />
              <Popover {...popover} tabIndex={-1}>
                <Icon icon={IconName.Clock} style={{ marginRight: spacing4 }} />
                <DateTimePickerContent
                  value={value}
                  showCalendar={showCalendar}
                  showTimePicker={showTimePicker}
                  showShortcuts={showShortcuts}
                  showMilliseconds={showMilliseconds}
                  shortcuts={shortcuts}
                  timePickerVariant={timePickerVariant}
                  timeSelectorIntervalMinutes={timeSelectorIntervalMinutes}
                  timeSelectorDropdownContentRef={timeSelectorDropdownContentRef}
                  customShortcutResolver={customShortcutResolver}
                  customEOD={customEOD}
                  onSelection={handleSelection}
                  onTabOut={handleTabOut}
                  minValue={minValue}
                  maxValue={maxValue}
                  defaultSelected={defaultSelected}
                />
              </Popover>
            </>
          }
        />
      </Popover>
    </DateTimePickerWrapper>
  );
});
